import * as React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

import { cn } from "@/lib/utils";

const cardClass =
  "relative flex h-full w-full flex-col justify-between overflow-hidden rounded-2xl bg-ink-950/[0.025] p-8 ring-1 ring-black/[0.06] transition-[background-color,box-shadow] duration-300 hover:bg-ink-950/[0.04] hover:shadow-lg";

export interface GradientCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  ctaText: string;
  ctaHref: string;
  imageUrl: string;
}

const cardAnimation = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.02, y: -4 },
};

const imageAnimation = {
  rest: { scale: 1, rotate: 0 },
  hover: { scale: 1.1, rotate: -3 },
};

/**
 * A soft card with a title, description and a link. The
 * illustration sits in the bottom-left corner (the page is RTL) and the card
 * clips the part that runs past its edges.
 */
const GradientCard = React.forwardRef<HTMLDivElement, GradientCardProps>(
  (
    { className, title, description, ctaText, ctaHref, imageUrl, ...props },
    ref,
  ) => (
    <motion.div
      variants={cardAnimation}
      initial="rest"
      whileHover="hover"
      animate="rest"
      className="h-full"
      ref={ref}
    >
      <div className={cn(cardClass, className)} {...props}>
        <motion.img
          src={imageUrl}
          alt=""
          loading="lazy"
          aria-hidden
          variants={imageAnimation}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
          // The illustrations sit on near-white, so multiply drops that box
          // into the card's tint.
          className="pointer-events-none absolute -bottom-[26%] -left-[30%] w-[110%] max-w-none opacity-90 mix-blend-multiply"
        />

        <div className="relative z-10 flex h-full flex-col">
          <div className="flex-grow">
            <h3 className="mb-2 text-[1.25rem] font-extrabold leading-[1.6] text-ink-950">{title}</h3>
            <p className="max-w-xs text-[13.5px] leading-[1.95] text-ink-700">{description}</p>
          </div>

          <Link
            to={ctaHref}
            className="group mt-6 inline-flex w-fit items-center gap-2 text-[13px] font-bold text-gerdoo-700 transition-colors hover:text-gerdoo-600"
          >
            {ctaText}
            <ArrowLeft
              className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1"
              aria-hidden
            />
          </Link>
        </div>
      </div>
    </motion.div>
  ),
);
GradientCard.displayName = "GradientCard";

export { GradientCard };
